import React from 'react';

/** Normalize an agent response into a single markdown string. */
export function formatChatContent(
  content: string | Array<{ text?: string; toolUse?: unknown }> | null | undefined,
): string {
  if (!content) return '';
  let text = '';
  if (typeof content === 'string') {
    text = content;
  } else if (Array.isArray(content)) {
    text = content
      .map((item) => (item && typeof item === 'object' && typeof item.text === 'string' ? item.text : ''))
      .filter(Boolean)
      .join('\n');
  }
  return text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

export const chatMarkdownComponents = {
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 className="mt-3 mb-2 text-base font-semibold text-[#0f172a]">{children}</h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="mt-3 mb-2 text-base font-semibold text-[#0f172a]">{children}</h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="mt-2 mb-2 text-sm font-semibold text-[#0f172a]">{children}</h3>
  ),
  h4: ({ children }: { children?: React.ReactNode }) => (
    <h4 className="mt-2 mb-1 text-sm font-semibold text-[#0f172a]">{children}</h4>
  ),
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="mb-2 leading-relaxed text-[#334155]">{children}</p>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="mb-2 list-disc pl-5 text-[#334155]">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="mb-2 list-decimal pl-5 text-[#334155]">{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => <li className="mb-1">{children}</li>,
  a: ({ children, href }: { children?: React.ReactNode; href?: string }) => {
    const isPageAnchor = href?.startsWith('#');
    return (
      <a
        href={href}
        target={isPageAnchor ? undefined : '_blank'}
        rel={isPageAnchor ? undefined : 'noopener noreferrer'}
        className="text-[#3251b7] underline hover:text-[#1e4474]"
      >
        {children}
      </a>
    );
  },
  code: ({ children }: { children?: React.ReactNode }) => (
    <code className="rounded bg-slate-100 px-1 py-0.5 text-[0.85em] text-[#0f172a]">{children}</code>
  ),
  pre: ({ children }: { children?: React.ReactNode }) => (
    <pre className="mb-2 overflow-x-auto rounded bg-slate-100 p-3 text-[0.85em] text-[#0f172a]">{children}</pre>
  ),
  table: ({ children }: { children?: React.ReactNode }) => (
    <div className="mb-3 overflow-x-auto rounded-lg border border-[#e1e8f0]">
      <table className="min-w-full border-collapse text-left text-sm text-[#334155]">{children}</table>
    </div>
  ),
  thead: ({ children }: { children?: React.ReactNode }) => (
    <thead className="bg-[#f8fafc] text-[#0f172a]">{children}</thead>
  ),
  tbody: ({ children }: { children?: React.ReactNode }) => <tbody>{children}</tbody>,
  tr: ({ children }: { children?: React.ReactNode }) => (
    <tr className="border-t border-[#e7e9ee] align-top">{children}</tr>
  ),
  th: ({ children }: { children?: React.ReactNode }) => (
    <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide">{children}</th>
  ),
  td: ({ children }: { children?: React.ReactNode }) => (
    <td className="px-3 py-2 leading-relaxed text-[#334155]">{children}</td>
  ),
} as const;
